import { DomainService, Domain } from '../../../../services/domain.ts';
import { ToastService } from '../../../../services/toast.ts';
import { authService, AuthService } from '../../../../services/auth.ts';
import '../../../../components/library/form/form-input.ts';
import '../../../../components/library/form/form-toggle.ts';
import '../../../../components/library/form/form-dynamic-list.ts';
import '../../../../components/library/form/form-select.ts';
import '../../../../components/library/form/form-textarea.ts';
import '../../../../components/library/ui/app-icon.ts';
import '../../../../components/library/ui/app-modal.ts';
import '../../../../components/library/ui/app-button.ts';
import '../../../../components/library/ui/app-badge.ts';
import '../../../../components/library/ui/app-card.ts';
import '../../../../components/gateway/rule-management.ts';
import { processDestinations } from '../../../../utils/backend-routing.ts';
import { scrollbarStyles } from '../../../../components/library/ui/shared-styles.ts';

import { OrganizationService } from '../../../../services/organization.ts';

export class GatewayDomainsTab extends HTMLElement {
    private domainService: DomainService;
    private orgService: OrganizationService;
    private toastService: ToastService;
    private authService: AuthService;
    private domains: Domain[] = [];
    private gateways: any[] = [];
    private isLoading: boolean = false;
    private _selectedDomainId: string | null = null;

    // Org ID passed from parent
    private _orgId: string = '';

    static get observedAttributes() {
        return ['org-id'];
    }

    private showModal: boolean = false;
    private editMode: boolean = false;
    private editingDomainId: string | null = null;
    private formData: any = null;
    private isSubmitting: boolean = false;

    private verifyModalOpen: boolean = false;
    private verifyingDomain: Domain | null = null;
    private verificationResult: 'success' | 'failure' | null = null;
    private verificationError: string = '';
    private sslActionLoading: string | null = null; // domain ID currently loading SSL action
    private _selectedVerificationGatewayId: string = 'shared';

    constructor() {
        super();
        this.domainService = DomainService.getInstance();
        this.orgService = OrganizationService.getInstance();
        this.toastService = ToastService.getInstance();
        this.authService = authService;
        this.attachShadow({ mode: 'open' });
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (name === 'org-id' && oldValue !== newValue) {
            this._orgId = newValue;
            this.loadData();
        }
    }

    connectedCallback() {
        this.render();
        // Data loading is handled by attributeChangedCallback when org-id is set
    }

    async loadData() {
        if (!this._orgId || this.isLoading) return;

        this.isLoading = true;
        this.render();
        try {
            const [domains, gateways] = await Promise.all([
                this.domainService.listByOrganization(this._orgId),
                this.orgService.getGateways(this._orgId)
            ]);
            this.domains = domains;
            this.gateways = gateways || [];
        } catch (e: any) {
            // Toast handled by ApiClient
        } finally {
            this.isLoading = false;
            this.render();
        }
    }

    private initFormData(domain?: Domain) {
        if (domain) {
            this.formData = {
                host: domain.host,
                description: domain.description || '',
                active: domain.active,
                routingStrategy: domain.details?.routing?.type || 'round_robin',
                destination: domain.details?.routing?.destinations || [],
                aiShieldEnabled: (domain.details as any)?.ai_shield?.enabled || false,
                aiShieldMode: (domain.details as any)?.ai_shield?.mode || 'strict',
                enableRateLimit: !!domain.details?.rate_limit,
                rateLimit: domain.details?.rate_limit?.limit || '',
                rateWindow: domain.details?.rate_limit?.rate || 'minute',
                multiplier: domain.details?.rate_limit?.multiplier || 1,
                identifierType: domain.details?.rate_limit?.identifier_type || 'ip',
                identifier: domain.details?.rate_limit?.identifier || '',
                config_waterfall_reset: domain.details?.routing?.config?.['limit_reset'] || 'hour',
                config_header_name: domain.details?.routing?.config?.['header_name'] || 'X-Routing-Key',
                allowPrivate: (domain.details?.routing as any)?.allow_private || false,
            };
        } else {
            this.formData = {
                host: '',
                description: '',
                active: true,
                routingStrategy: 'round_robin',
                destination: [],
                aiShieldEnabled: false,
                aiShieldMode: 'strict',
                enableRateLimit: false,
                rateLimit: '',
                rateWindow: 'minute',
                multiplier: 1,
                identifierType: 'ip',
                identifier: '',
                config_waterfall_reset: 'hour',
                config_header_name: 'X-Routing-Key',
            };
        }
    }

    private handleFormChange(e: any) {
        if (!this.formData) return;
        const target = e.target;
        const name = target.getAttribute('name');
        if (!name) return;

        if (target.tagName === 'FORM-TOGGLE') {
            this.formData[name] = target.checked;
            // Immediate re-render for UI section toggling
            if (name === 'enableRateLimit' || name === 'aiShieldEnabled' || name === 'active' || name === 'allowPrivate') {
                this.render();
            }
        } else if (target.tagName === 'FORM-DYNAMIC-LIST') {
            this.formData[name] = target.values;
            // Also check if strategy changed to update dynamic list layout
        } else if (target.tagName === 'FORM-SELECT') {
            this.formData[name] = target.value;
            if (name === 'routingStrategy') {
                this.render();
            }
        } else {
            this.formData[name] = target.value;
        }
    }

    private validateDestinations(destinations: string[]): boolean {
        const broadUrlRegex = /^(https?:\/\/)?([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*|localhost|(\d{1,3}\.){3}\d{1,3})(:\d{1,5})?(\/.*)?$/;

        for (const dest of destinations) {
            if (!broadUrlRegex.test(dest)) {
                return false;
            }
        }
        return true;
    }

    private cleanHost(host: string): string {
        if (!host) return '';
        let h = host.trim();
        // Remove protocol if present
        if (h.includes('://')) {
            try {
                const url = new URL(h);
                h = url.hostname;
            } catch (e) {
                // If it's not a valid URL, just strip the protocol manually
                h = h.split('://')[1].split('/')[0].split(':')[0];
            }
        } else {
            // Strip path and port if present
            h = h.split('/')[0].split(':')[0];
        }
        return h;
    }

    async handleCreateDomain() {
        if (!this.formData || this.isSubmitting) return;
        this.isSubmitting = true;
        this.render();

        try {
            const { host: rawHost, description, active, routingStrategy, destination, aiShieldEnabled, aiShieldMode, enableRateLimit, rateLimit, rateWindow, multiplier, identifierType, identifier, config_waterfall_reset, config_header_name } = this.formData;
            const host = this.cleanHost(rawHost);
            const { destinations, weights, limits, mappings } = processDestinations(destination);

            if (destinations.length === 0) {
                this.toastService.warning('At least one destination is required.');
                return;
            }

            if (!this.validateDestinations(destinations)) {
                this.toastService.warning('Invalid destination. Must be a URL, IP, or hostname.');
                return;
            }

            let rateLimitDetails = undefined;
            if (enableRateLimit) {
                const limitNum = parseInt(rateLimit);
                if (isNaN(limitNum) || limitNum <= 0) {
                    this.toastService.warning('Invalid rate limit. Must be a positive number.');
                    return;
                }
                rateLimitDetails = {
                    limit: limitNum,
                    rate: rateWindow,
                    multiplier: parseInt(multiplier) || 1,
                    identifier_type: identifierType,
                    identifier: identifier
                };
            }

            const routingConfig: any = {};
            if (routingStrategy === 'header' || routingStrategy === 'header_match') {
                if (config_header_name) routingConfig['header_name'] = config_header_name;
            } else if (routingStrategy === 'waterfall') {
                if (config_waterfall_reset) routingConfig['limit_reset'] = config_waterfall_reset;
            }

            const newDomain: Domain = {
                host,
                description,
                active,
                details: {
                    routing: {
                        type: routingStrategy,
                        destinations: destinations,
                        config: Object.keys(routingConfig).length > 0 ? routingConfig : undefined,
                        weights: Object.keys(weights).length > 0 ? weights : undefined,
                        limits: Object.keys(limits).length > 0 ? limits : undefined,
                        mappings: Object.keys(mappings).length > 0 ? mappings : undefined
                    },
                    rate_limit: rateLimitDetails,
                    ai_shield: {
                        enabled: aiShieldEnabled,
                        mode: aiShieldMode || 'strict'
                    }
                }
            };

            const created = await this.domainService.create(newDomain, this._orgId);
            this.showModal = false;
            this.formData = null;
            await this.loadData();

            if (created) {
                await this.handleVerifyDomain(created);
            }
        } catch (e: any) {
            // Toast handled by ApiClient
        } finally {
            this.isSubmitting = false;
            this.render();
        }
    }

    async handleUpdateDomain() {
        if (!this.editingDomainId || !this.formData || this.isSubmitting) return;
        this.isSubmitting = true;
        this.render();

        try {
            const { host: rawHost, description, active, routingStrategy, destination, aiShieldEnabled, aiShieldMode, enableRateLimit, rateLimit, rateWindow, multiplier, identifierType, identifier, config_waterfall_reset, config_header_name } = this.formData;
            const host = this.cleanHost(rawHost);
            const { destinations, weights, limits, mappings } = processDestinations(destination);

            if (destinations.length === 0) {
                this.toastService.warning('At least one destination is required.');
                return;
            }

            if (!this.validateDestinations(destinations)) {
                this.toastService.warning('Invalid destination. Must be a URL, IP, or hostname.');
                return;
            }

            let rateLimitDetails = undefined;
            if (enableRateLimit) {
                const limitNum = parseInt(rateLimit);
                if (isNaN(limitNum) || limitNum <= 0) {
                    this.toastService.warning('Invalid rate limit. Must be a positive number.');
                    return;
                }
                rateLimitDetails = {
                    limit: limitNum,
                    rate: rateWindow,
                    multiplier: parseInt(multiplier) || 1,
                    identifier_type: identifierType,
                    identifier: identifier
                };
            }

            const routingConfig: any = {};
            if (routingStrategy === 'header' || routingStrategy === 'header_match') {
                if (config_header_name) routingConfig['header_name'] = config_header_name;
            } else if (routingStrategy === 'waterfall') {
                if (config_waterfall_reset) routingConfig['limit_reset'] = config_waterfall_reset;
            }

            const updatedDomain: Domain = {
                host,
                description,
                active,
                details: {
                    routing: {
                        type: routingStrategy,
                        destinations: destinations,
                        config: Object.keys(routingConfig).length > 0 ? routingConfig : undefined,
                        weights: Object.keys(weights).length > 0 ? weights : undefined,
                        limits: Object.keys(limits).length > 0 ? limits : undefined,
                        mappings: Object.keys(mappings).length > 0 ? mappings : undefined
                    },
                    rate_limit: rateLimitDetails,
                    ai_shield: {
                        enabled: aiShieldEnabled,
                        mode: aiShieldMode || 'strict'
                    }
                }
            };

            await this.domainService.update(this.editingDomainId, updatedDomain, this._orgId);
            this.showModal = false;
            this.formData = null;
            this.editMode = false;
            this.editingDomainId = null;
            await this.loadData();
        } catch (e: any) {
            // Toast handled by ApiClient
        } finally {
            this.isSubmitting = false;
            this.render();
        }
    }

    async handleDeleteDomain(domainId: string) {
        if (!confirm('Are you sure you want to delete this domain? This action cannot be undone.')) {
            return;
        }
        try {
            await this.domainService.delete(domainId);
            if (this.verifyingDomain?.id === domainId) {
                this.verifyingDomain = null;
                this.verifyModalOpen = false;
            }
            await this.loadData();
        } catch (e: any) {
            // Toast handled by ApiClient
        }
    }

    async handleVerifyDomain(domain: Domain) {
        this.verifyingDomain = domain;
        this.verifyModalOpen = true;
        this.verificationResult = null;
        this.verificationError = '';
        this.render();
    }

    async performVerification() {
        if (!this.verifyingDomain || !this.verifyingDomain.id) return;

        this.isLoading = true;
        this.render();

        try {
            await this.domainService.verify(this.verifyingDomain.id);
            this.verificationResult = 'success';
            this.verifyModalOpen = false;
            await this.loadData();
        } catch (e: any) {
            this.verificationResult = 'failure';
            // Toast handled by ApiClient
        } finally {
            this.isLoading = false;
            this.render();
        }
    }

    async handleObtainCertificate(domainId: string) {
        if (this.sslActionLoading) return;
        this.sslActionLoading = domainId;
        this.render();

        try {
            await this.domainService.obtainCertificate(domainId);
            this.toastService.success('SSL certificate obtained successfully');
            await this.loadData();
        } catch (e: any) {
            this.toastService.error(e.message || 'Failed to obtain SSL certificate');
        } finally {
            this.sslActionLoading = null;
            this.render();
        }
    }

    async handleRenewCertificate(domainId: string) {
        if (this.sslActionLoading) return;
        this.sslActionLoading = domainId;
        this.render();

        try {
            await this.domainService.renewCertificate(domainId);
            this.toastService.success('SSL certificate renewed successfully');
            await this.loadData();
        } catch (e: any) {
            this.toastService.error(e.message || 'Failed to renew SSL certificate');
        } finally {
            this.sslActionLoading = null;
            this.render();
        }
    }

    private copyToClipboard(text: string, btnId: string) {
        navigator.clipboard.writeText(text).then(() => {
            const btn = this.shadowRoot?.getElementById(btnId);
            if (btn) {
                const originalContent = btn.innerHTML;
                btn.innerHTML = '<span style="color:#10b981; font-size: 0.75rem;">Copied!</span>';
                setTimeout(() => {
                    if (this.shadowRoot?.getElementById(btnId)) {
                        btn.innerHTML = originalContent;
                    }
                }, 2000);
            }
        });
    }

    private _renderTimeout: any = null;

    render() {
        if (this._renderTimeout) return;
        this._renderTimeout = setTimeout(() => {
            this._renderInternal();
            this._renderTimeout = null;
        }, 0);
    }

    private _renderInternal() {
        if (!this.shadowRoot) return;

        // Initialize structure once
        if (!this.shadowRoot.querySelector('.domains-container')) {
            this.shadowRoot.innerHTML = `
                <div class="domains-container"></div>
                <div class="modal-container"></div>
                <div class="verify-modal-container"></div>
            `;
        }

        const domainsContainer = this.shadowRoot.querySelector('.domains-container')!;
        const modalContainer = this.shadowRoot.querySelector('.modal-container')!;
        const verifyModalContainer = this.shadowRoot.querySelector('.verify-modal-container')!;

        if (this._selectedDomainId) {
            const domain = this.domains.find(d => d.id === this._selectedDomainId);
            this.dispatchEvent(new CustomEvent('breadcrumb-change', { detail: domain?.host || 'Unknown', bubbles: true, composed: true }));
            
            domainsContainer.innerHTML = `
                <style>
                    :host { display: block; color: white; }
                </style>
                <rule-management domain-id="${this._selectedDomainId}"></rule-management>
            `;
            
            const ruleMgmt = domainsContainer.querySelector('rule-management');
            ruleMgmt?.addEventListener('back', () => {
                this._selectedDomainId = null;
                this.dispatchEvent(new CustomEvent('breadcrumb-change', { detail: null, bubbles: true, composed: true }));
                this.render();
            });
            // Clear modals when in rule management view
            modalContainer.innerHTML = '';
            verifyModalContainer.innerHTML = '';
            return;
        }

        const editingDomain = this.editMode ? this.domains.find(d => d.id === this.editingDomainId) : null;

        domainsContainer.innerHTML = `
            <style>
                :host {
                    display: block;
                    color: white;
                }

                @media (max-width: 768px) {
                    :host {
                        padding: 1rem;
                    }
                    .header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 1rem;
                    }
                    .btn-add {
                        width: 100%;
                        justify-content: center;
                    }
                }

                .header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 2rem;
                }

                h1 {
                    font-size: 1.5rem;
                    font-weight: 700;
                    margin: 0;
                    letter-spacing: -0.02em;
                    color: var(--accent, #f59e0b);
                    text-transform: uppercase;
                }

                .grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                    gap: 1.5rem;
                }
                
                .domain-card {
                    --border-sketch: var(--border);
                    --bg-card: rgba(255, 255, 255, 0.015);
                    transition: all 0.2s ease;
                }
                
                .domain-card:hover {
                    --border-sketch: var(--accent);
                    --bg-card: rgba(255, 255, 255, 0.03);
                    transform: translateY(-2px);
                }

                .card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 1rem;
                }

                .host {
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: white;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    white-space: nowrap;
                    min-width: 0;
                    flex: 1;
                }

                .description {
                    font-size: 0.9rem;
                    color: #94a3b8;
                    margin-bottom: 1.5rem;
                    line-height: 1.5;
                }

                .card-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border-top: 1px solid rgba(255, 255, 255, 0.05);
                    padding-top: 1rem;
                    margin-top: 1rem;
                }

                .empty-state {
                    text-align: center;
                    padding: 4rem 2rem;
                    background: rgba(255, 255, 255, 0.02);
                    border-radius: 12px;
                    border: 1px dashed rgba(255, 255, 255, 0.1);
                }

                .loader {
                    width: 40px;
                    height: 40px;
                    border: 3px solid rgba(245, 158, 11, 0.1);
                    border-top-color: var(--accent, #f59e0b);
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin: 2rem auto;
                }

                .btn-copy {
                    background: transparent;
                    border: none;
                    color: #94a3b8;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    padding: 4px;
                    border-radius: 4px;
                    transition: all 0.2s;
                }
                .btn-copy:hover {
                    color: white;
                    background: rgba(255, 255, 255, 0.1);
                }

                ::-webkit-scrollbar {
                    width: 12px;
                    height: 12px;
                }

                ::-webkit-scrollbar-track {
                    background: rgba(0, 0, 0, 0.2);
                }

                ::-webkit-scrollbar-thumb {
                    background: rgba(245, 158, 11, 0.2);
                    border-radius: 20px;
                    border: 3px solid transparent;
                    background-clip: content-box;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: rgba(245, 158, 11, 0.5);
                    background-clip: content-box;
                }
                ${scrollbarStyles}
            </style>

            <div style="display: flex; justify-content: flex-end; align-items: center; margin-bottom: 24px;">
                <app-button variant="white" size="sm" id="addDomain" icon="plus"></app-button>
            </div>

            ${this.isLoading ? '<div class="loader"></div>' : ''}
            
            ${!this.isLoading && this.domains.length === 0 ? `
                <div class="empty-state">
                    <h3>No Active Domains</h3>
                    <p>Register a new domain to begin routing traffic through Crate.</p>
                </div>
            ` : ''}

            <div class="grid">
                ${this.domains.map(domain => `
                    <app-card data-domain-id="${domain.id}" style="min-height: 180px; display: flex; flex-direction: column; justify-content: space-between;">
                        <div>
                            <div class="card-header" style="min-width: 0; margin-bottom: 0.5rem;">
                                <div style="flex: 1; min-width: 0;">
                                    <div class="host" data-tooltip="${domain.host}">${domain.host}</div>
                                </div>
                                <div style="display: flex; gap: 0.75rem; align-items: center; margin-top: 4px;">
                                    <div data-tooltip="${domain.verified ? 'Verified' : 'Unverified'}" style="color: ${domain.verified ? '#10b981' : '#64748b'}; display: flex; align-items: center; cursor: help;">
                                        <app-icon name="${domain.verified ? 'check' : 'x'}" size="sm"></app-icon>
                                    </div>
                                    <div data-tooltip="${domain.active ? 'Active' : 'Inactive'}" style="color: ${domain.active ? '#10b981' : '#64748b'}; display: flex; align-items: center; cursor: help;">
                                        <app-icon name="power" size="sm"></app-icon>
                                    </div>
                                    ${domain.ssl_status === 'active' ? `
                                        <div data-tooltip="SSL Secure" style="color: #10b981; display: flex; align-items: center; cursor: help;">
                                            <app-icon name="lock" size="sm"></app-icon>
                                        </div>
                                    ` : ''}
                                    ${domain.ssl_status === 'pending' ? `
                                        <div data-tooltip="SSL Issuance Pending" style="color: #eab308; display: flex; align-items: center; cursor: help;">
                                            <div class="loader" style="width: 14px; height: 14px; border-width: 2px; border-color: currentColor; border-top-color: transparent; margin: 0;"></div>
                                        </div>
                                    ` : ''}
                                    ${domain.ssl_status === 'expired' ? `
                                        <div data-tooltip="SSL Expired / Insecure" style="color: #ef4444; display: flex; align-items: center; cursor: help;">
                                            <app-icon name="unlock" size="sm"></app-icon>
                                        </div>
                                    ` : ''}
                                    ${domain.verified && (!domain.ssl_status || domain.ssl_status === 'none') ? `
                                        <div title="SSL Not Configured" style="color: #64748b; display: flex; align-items: center; cursor: help;">
                                            <app-icon name="unlock" size="sm"></app-icon>
                                        </div>
                                    ` : ''}
                                </div>
                            </div>

                            <div style="display: flex; align-items: center; justify-content: space-between; gap: 1rem; border-bottom: 1px solid rgba(255,255,255,0.05); padding-bottom: 0.75rem; margin-bottom: 1rem;">
                                <div style="font-family: monospace; font-size: 0.7rem; color: #475569; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1;">
                                    ${domain.id}
                                </div>
                                <button class="btn-copy" id="copy-id-${domain.id}" data-id="${domain.id}" data-tooltip="Copy ID" style="padding: 2px; color: #475569;">
                                    <app-icon name="copy" size="xs"></app-icon>
                                </button>
                            </div>

                            <div class="description">${domain.description || 'No description provided.'}</div>

                            ${domain.details?.rate_limit ? `
                                <div style="font-size: 0.8rem; color: #94a3b8; margin-bottom: 1rem;">
                                    <span style="color: var(--accent, #f59e0b); font-weight: 600;">Rate Limit:</span> 
                                    ${domain.details.rate_limit.limit} req / ${domain.details.rate_limit.rate}
                                </div>
                            ` : ''}
                        </div>
                        <div class="card-footer">
                            <div style="display: flex; gap: 0.5rem;">
                                <app-button variant="secondary" size="sm" data-edit-id="${domain.id}" icon="edit" square data-tooltip="Edit Domain"></app-button>
                                ${!domain.verified ? `<app-button variant="secondary" size="sm" style="color: #eab308; border-color: rgba(234, 179, 8, 0.3);" data-verify-id="${domain.id}" icon="check" square data-tooltip="Verify Domain"></app-button>` : ''}
                                ${domain.verified && (!domain.ssl_status || domain.ssl_status === 'none') ? `<app-button variant="secondary" size="sm" style="color: #3b82f6; border-color: rgba(59, 130, 246, 0.3);" data-ssl-id="${domain.id}" icon="lock" square data-tooltip="Obtain SSL Certificate" ${this.sslActionLoading === domain.id ? 'loading disabled' : ''}></app-button>` : ''}
                                ${domain.verified && domain.ssl_status === 'expired' ? `<app-button variant="secondary" size="sm" style="color: #ef4444; border-color: rgba(239, 68, 68, 0.3);" data-ssl-id="${domain.id}" icon="lock" square data-tooltip="Renew Expired SSL" ${this.sslActionLoading === domain.id ? 'loading disabled' : ''}></app-button>` : ''}
                                ${domain.verified && domain.ssl_status === 'active' ? `<app-button variant="secondary" size="sm" style="color: #10b981; border-color: rgba(16, 185, 129, 0.3);" data-renew-ssl-id="${domain.id}" icon="lock" square data-tooltip="Renew SSL Certificate" ${this.sslActionLoading === domain.id ? 'loading disabled' : ''}></app-button>` : ''}
                                <app-button variant="danger" size="sm" data-delete-id="${domain.id}" icon="trash" square data-tooltip="Delete Domain"></app-button>
                            </div>
                            <app-button variant="white" size="sm" data-route-id="${domain.id}" icon="route" square data-tooltip="View Rules"></app-button>
                        </div>
                    </app-card>
                `).join('')}
            </div>
        `;

        // Only update modal container if modal state changed or modal is open
        if (this.showModal) {
            this.renderEditModal(modalContainer);
        } else {
            modalContainer.innerHTML = '';
        }

        if (this.verifyModalOpen) {
            this.renderVerifyModal(verifyModalContainer);
        } else {
            verifyModalContainer.innerHTML = '';
        }

        this.setupEventListeners();
    }

    private renderEditModal(container: Element) {
        const currentRoutingType = this.formData?.routingStrategy || 'round_robin';
        const isWeighted = (currentRoutingType === 'weighted_round_robin');
        const isWaterfall = (currentRoutingType === 'waterfall');
        const isHeaderMatch = currentRoutingType === 'header_match';

        // Check if we already have the modal to avoid recreating it
        let modal = container.querySelector('app-modal#domainModal');
        if (!modal) {
            container.innerHTML = `
                <app-modal id="domainModal" open>
                    <div slot="header" style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                        <h2 style="margin: 0; color: var(--accent);">${this.editMode ? 'Edit Domain' : 'Create New Domain'}</h2>
                        <div class="header-toggle-container"></div>
                    </div>

                    <form id="domainForm">
                         <div class="form-fields"></div>
                    </form>

                    <div slot="actions">
                        <div style="display: flex; gap: 0.5rem; justify-content: flex-end; width: 100%;">
                            <app-button variant="ghost" size="sm" id="cancelBtn" icon="x" data-tooltip="Cancel"></app-button>
                            <app-button variant="primary" size="sm" id="saveDomainBtn" icon="check" ${this.isSubmitting ? 'loading' : ''} data-tooltip="${this.editMode ? 'Save Changes' : 'Create Domain'}"></app-button>
                        </div>
                    </div>
                </app-modal>
            `;
            modal = container.querySelector('app-modal#domainModal');
        }

        const headerToggleContainer = modal!.querySelector('.header-toggle-container')!;
        headerToggleContainer.innerHTML = `<form-toggle name="active" label="Active" style="margin-bottom: 0;" ${this.formData?.active ? 'checked' : ''}></form-toggle>`;

        const formFields = modal!.querySelector('.form-fields')!;
        formFields.innerHTML = `
            <style>
                .section-title { font-size: 1rem; color: var(--accent, #f59e0b); margin: 1.5rem 0 1rem 0; font-weight: 600; text-transform: uppercase; border-bottom: 1px solid rgba(255, 255, 255, 0.1); padding-bottom: 0.5rem; }
            </style>
             <form-input name="host" label="Domain Host" placeholder="api.example.com" required 
                value="${this.formData?.host || ''}" ${this.editMode ? 'disabled' : ''}></form-input>

             <form-textarea name="description" label="Description" placeholder="Production API gateway" 
                value="${this.formData?.description || ''}"></form-textarea>
             
             <div class="section-title">Routing Configuration</div>
             
             <form-select id="routingStrategy" name="routingStrategy" label="Routing Algorithm" 
                options='[
                    {"value":"round_robin","label":"Round Robin (Default)"},
                    {"value":"weighted_round_robin","label":"Weighted Round Robin"},
                    {"value":"waterfall","label":"Waterfall (Priority)"},
                    {"value":"header","label":"Header Based (Sticky)"},
                    {"value":"header_match","label":"Header Match (Explicit)"},
                    {"value":"power_of_two_choices","label":"Power of Two Choices (Least Loaded)"}
                ]'
                value="${this.formData?.routingStrategy || 'round_robin'}"></form-select>

             <form-dynamic-list id="destinationsList" name="destination" label="Destination(s) *" placeholder="10.0.0.1:8080" 
                values='${JSON.stringify(this.formData?.destination || [])}'
                ${isWeighted ? 'with-weights' : ''}
                ${isWaterfall ? 'with-limits' : ''}
                ${isHeaderMatch ? 'with-mappings' : ''}
                ></form-dynamic-list>



             <div id="routingConfigContainer">
                ${currentRoutingType === 'weighted_round_robin' ? `
                    <div id="config_weighted_round_robin" style="padding-left: 1rem; border-left: 2px solid var(--accent, #f59e0b); margin-bottom: 1rem;">
                        <p style="font-size: 0.8rem; color: #94a3b8; margin-bottom: 0.5rem;">
                            Use the list above to set weights for each destination (1-100).
                        </p>
                    </div>
                ` : ''}

                ${currentRoutingType === 'waterfall' ? `
                    <div id="config_waterfall" style="padding-left: 1rem; border-left: 2px solid var(--accent, #f59e0b); margin-bottom: 1rem;">
                        <form-select name="config_waterfall_reset" label="Limit Reset Frequency" 
                            options='[
                                {"value":"minute","label":"Every Minute"},
                                {"value":"hour","label":"Every Hour"},
                                {"value":"day","label":"Every Day"},
                                {"value":"month","label":"Every Month"}
                            ]'
                            value="${this.formData?.config_waterfall_reset || 'hour'}"></form-select>
                    </div>
                ` : ''}

                ${currentRoutingType === 'header' || currentRoutingType === 'header_match' ? `
                    <div id="config_header" style="padding-left: 1rem; border-left: 2px solid var(--accent, #f59e0b); margin-bottom: 1rem;">
                        <form-input name="config_header_name" label="Header Name" placeholder="X-User-ID" 
                            value="${this.formData?.config_header_name || 'X-Routing-Key'}"></form-input>
                    </div>
                ` : ''}
             </div>

             <div class="section-title">AI Shield</div>
             <form-toggle id="enableAIShield" name="aiShieldEnabled" label="Enable AI Bot Protection"
                ${this.formData?.aiShieldEnabled ? 'checked' : ''}></form-toggle>
             
             <div id="aiShieldFields" style="display: ${this.formData?.aiShieldEnabled ? 'block' : 'none'}; padding-left: 1.5rem; border-left: 2px solid rgba(255,255,255,0.1); margin-top: 1rem;">
                <form-select name="aiShieldMode" label="Protection Mode"
                    options='[{"value":"strict","label":"Strict Rate Limit (5/req min)"},{"value":"block","label":"Block Access"}]'
                    value="${this.formData?.aiShieldMode || 'strict'}"></form-select>
             </div>

             <div class="section-title">Rate Limiting</div>

             <form-toggle id="enableRateLimit" name="enableRateLimit" label="Enable Rate Limiting" 
                ${this.formData?.enableRateLimit ? 'checked' : ''}></form-toggle>

             <div id="rateLimitFields" style="display: ${this.formData?.enableRateLimit ? 'block' : 'none'}; padding-left: 1.5rem; border-left: 2px solid rgba(255,255,255,0.1); margin-top: 1rem;">
                <form-input type="number" name="rateLimit" label="Request Limit" min="1" placeholder="e.g. 1000"
                    value="${this.formData?.rateLimit || ''}"></form-input>
                
                <form-select name="rateWindow" label="Time Window" 
                    options='[{"value":"second","label":"Per Second"},{"value":"minute","label":"Per Minute"},{"value":"hour","label":"Per Hour"},{"value":"day","label":"Per Day"}]'
                    value="${this.formData?.rateWindow || 'minute'}"></form-select>
                
                <form-input type="number" name="multiplier" label="Window Multiplier" min="1" placeholder="e.g. 1"
                    value="${this.formData?.multiplier || 1}"></form-input>
                
                <form-select name="identifierType" label="Identifier Location"
                    options='[{"value":"header","label":"Request Header"},{"value":"query","label":"Query Parameter"},{"value":"cookie","label":"Cookie"},{"value":"ip","label":"IP Address"},{"value":"referer","label":"Referer"},{"value":"body","label":"Request Body"},{"value":"path","label":"URL Path"}]'
                    value="${this.formData?.identifierType || 'ip'}"></form-select>

                <form-input name="identifier" label="Identifier Key" placeholder="e.g. X-API-Key"
                     value="${this.formData?.identifier || ''}"></form-input>
             </div>
        `;
    }

    private renderVerifyModal(container: Element) {
        let txtHost = '';
        let cnameHost = '';
        if (this.verifyingDomain?.host) {
            const parts = this.verifyingDomain.host.split('.');
            const sub = parts.length > 2 ? parts.slice(0, -2).join('.') : '@';
            txtHost = sub === '@' ? '_crate-verify' : `_crate-verify.${sub}`;
            cnameHost = sub;
        }

        const gatewayOptions = [
            { value: 'shared', label: 'Shared Edge Network (Recommended)' }
        ];
        
        for (const gw of this.gateways) {
            if (gw.type === 'private_managed' || gw.type === 'private_byoc') {
                gatewayOptions.push({
                    value: gw.id,
                    label: `${gw.name} (${gw.type === 'private_managed' ? 'Dedicated Node' : 'BYOC Node'})`
                });
            }
        }

        const selectedGateway = this._selectedVerificationGatewayId === 'shared' ? null : this.gateways.find(g => g.id === this._selectedVerificationGatewayId);
        const isCname = this._selectedVerificationGatewayId === 'shared' || (selectedGateway && !selectedGateway.address?.match(/^[0-9.]+$/));
        const recordType = isCname ? 'CNAME' : 'A';
        const targetValue = this._selectedVerificationGatewayId === 'shared' ? 'us-east-1.gateway.crate.cc' : (selectedGateway?.address || '[Pending Node IP Assignment]');

        container.innerHTML = `
            <app-modal id="verifyModal" open>
                <h2 slot="header" style="color: var(--accent);">Verify Domain Ownership</h2>
                
                <div style="margin-top: 1rem; color: #cbd5e1; font-size: 0.95rem; line-height: 1.6;">
                    <p>To verify ownership of <strong>${this.verifyingDomain?.host}</strong>, please add the following TXT record to your DNS configuration:</p>
                    
                    <div style="background: rgba(0, 0, 0, 0.25); border: 1px solid rgba(255, 255, 255, 0.05); padding: 1.25rem; border-radius: 12px; font-family: monospace; margin: 1.5rem 0;">
                        
                        <div style="margin-bottom: 1.25rem;">
                            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.35rem;">
                                <span style="color: #94a3b8; font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;">TXT Host / Name</span>
                                <button class="btn-copy" id="copyTxtHost" style="display: flex; align-items: center; gap: 0.25rem; font-size: 0.75rem; font-family: inherit;">
                                    <app-icon name="copy" size="xs"></app-icon> Copy
                                </button>
                            </div>
                            <div style="color: white; font-size: 0.9rem; word-break: break-all; padding: 0.5rem; background: rgba(255,255,255,0.02); border-radius: 6px; border: 1px solid rgba(255,255,255,0.02);">${txtHost}</div>
                        </div>

                        <div>
                            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.35rem;">
                                <span style="color: #94a3b8; font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;">TXT Value</span>
                                <button class="btn-copy" id="copyTxtValue" style="display: flex; align-items: center; gap: 0.25rem; font-size: 0.75rem; font-family: inherit;">
                                    <app-icon name="copy" size="xs"></app-icon> Copy
                                </button>
                            </div>
                            <div style="color: var(--accent); font-size: 0.9rem; word-break: break-all; padding: 0.5rem; background: rgba(255,255,255,0.02); border-radius: 6px; border: 1px solid rgba(255,255,255,0.02); font-weight: 600;">${this.verifyingDomain?.verification_code}</div>
                        </div>

                    </div>

                    ${gatewayOptions.length > 1 ? `
                        <p style="margin-top: 1.5rem; margin-bottom: 0.5rem;">Additionally, please route traffic through your gateway node:</p>
                        <form-select id="verificationGatewayTarget"
                            options='${JSON.stringify(gatewayOptions)}'
                            value="${this._selectedVerificationGatewayId}">
                        </form-select>
                    ` : `
                        <p style="margin-top: 1.5rem;">Additionally, please create a CNAME record to route traffic through our global gateway edge:</p>
                    `}
                    
                    <div style="background: rgba(0, 0, 0, 0.25); border: 1px solid rgba(255, 255, 255, 0.05); padding: 1.25rem; border-radius: 12px; font-family: monospace; margin: 1.5rem 0;">
                        
                        <div style="margin-bottom: 1.25rem;">
                            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.35rem;">
                                <span class="record-type-label" style="color: #94a3b8; font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;">${recordType} Host / Name</span>
                                <button class="btn-copy" id="copyCnameHost" style="display: flex; align-items: center; gap: 0.25rem; font-size: 0.75rem; font-family: inherit;">
                                    <app-icon name="copy" size="xs"></app-icon> Copy
                                </button>
                            </div>
                            <div style="color: white; font-size: 0.9rem; word-break: break-all; padding: 0.5rem; background: rgba(255,255,255,0.02); border-radius: 6px; border: 1px solid rgba(255,255,255,0.02);">${cnameHost}</div>
                        </div>

                        <div>
                            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.35rem;">
                                <span class="record-type-label" style="color: #94a3b8; font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;">${recordType} Value / Target</span>
                                <button class="btn-copy" id="copyCnameValue" style="display: flex; align-items: center; gap: 0.25rem; font-size: 0.75rem; font-family: inherit;">
                                    <app-icon name="copy" size="xs"></app-icon> Copy
                                </button>
                            </div>
                            <div id="dns-target-value" style="color: var(--accent); font-size: 0.9rem; word-break: break-all; padding: 0.5rem; background: rgba(255,255,255,0.02); border-radius: 6px; border: 1px solid rgba(255,255,255,0.02); font-weight: 600;">${targetValue}</div>
                        </div>

                    </div>

                    ${this.verificationResult === 'failure' ? `
                        <div class="verification-result failure" style="margin-top: 1rem;">
                            <span style="font-weight: 600;">Verification Failed:</span> Please make sure your TXT record is correctly configured and has propagated.
                        </div>
                    ` : ''}

                    ${this.verificationResult === 'success' ? `
                        <div class="verification-result success" style="margin-top: 1rem;">
                            <span style="font-weight: 600;">Success!</span> Domain ownership verified.
                        </div>
                    ` : ''}
                </div>

                <div slot="actions">
                    <div style="display: flex; gap: 0.5rem; justify-content: flex-end; width: 100%;">
                        <app-button variant="ghost" size="sm" id="closeVerifyBtn" icon="x" data-tooltip="Close">Close</app-button>
                        <app-button variant="primary" size="sm" id="performVerifyBtn" icon="check" data-tooltip="Verify DNS Record" ${this.isLoading || this.verificationResult === 'success' ? 'disabled' : ''}>
                            ${this.isLoading ? 'Verifying...' : 'Verify DNS Record'}
                        </app-button>
                    </div>
                </div>
            </app-modal>
        `;
    }


    setupEventListeners() {
        const shadow = this.shadowRoot;
        if (!shadow) return;

        shadow.getElementById('addDomain')?.addEventListener('click', () => {
            this.editMode = false;
            this.editingDomainId = null;
            this.initFormData();
            this.showModal = true;
            this.render();
        });

        shadow.getElementById('cancelBtn')?.addEventListener('click', () => {
            this.showModal = false;
            this.formData = null;
            this.render();
        });

        shadow.getElementById('domainForm')?.addEventListener('change', (e) => {
            this.handleFormChange(e);
        });

        shadow.getElementById('domainForm')?.addEventListener('input', (e) => {
            this.handleFormChange(e);
        });

        shadow.getElementById('saveDomainBtn')?.addEventListener('click', () => {
            (shadow.getElementById('domainForm') as HTMLFormElement)?.requestSubmit();
        });

        shadow.getElementById('domainForm')?.addEventListener('submit', async (e) => {
            e.preventDefault();
            if (this.editMode) {
                await this.handleUpdateDomain();
            } else {
                await this.handleCreateDomain();
            }
        });

        shadow.querySelectorAll('app-button[data-route-id]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const domainId = (btn as HTMLElement).getAttribute('data-route-id');
                if (domainId) {
                    this._selectedDomainId = domainId;
                    this.render();
                    
                    this.dispatchEvent(new CustomEvent('domain-selected', {
                        detail: { domainId },
                        bubbles: true,
                        composed: true
                    }));
                }
            });
        });

        shadow.querySelectorAll('app-button[data-delete-id]').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                e.stopPropagation();
                const domainId = (btn as HTMLElement).getAttribute('data-delete-id');
                if (domainId) await this.handleDeleteDomain(domainId);
            });
        });

        shadow.querySelectorAll('app-button[data-edit-id]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const domainId = (btn as HTMLElement).getAttribute('data-edit-id');
                if (domainId) {
                    const d = this.domains.find(d => d.id === domainId);
                    if (d) {
                        this.editMode = true;
                        this.editingDomainId = domainId;
                        this.initFormData(d);
                        this.showModal = true;
                        this.render();
                    }
                }
            });
        });

        shadow.querySelectorAll('app-button[data-verify-id]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const domainId = (btn as HTMLElement).getAttribute('data-verify-id');
                if (domainId) {
                    const d = this.domains.find(d => d.id === domainId);
                    if (d) this.handleVerifyDomain(d);
                }
            });
        });

        shadow.querySelectorAll('app-button[data-ssl-id]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const domainId = (btn as HTMLElement).getAttribute('data-ssl-id');
                if (domainId) this.handleObtainCertificate(domainId);
            });
        });

        shadow.querySelectorAll('app-button[data-renew-ssl-id]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const domainId = (btn as HTMLElement).getAttribute('data-renew-ssl-id');
                if (domainId) this.handleRenewCertificate(domainId);
            });
        });

        shadow.getElementById('closeVerifyBtn')?.addEventListener('click', () => {
            this.verifyModalOpen = false;
            this.render();
        });

        shadow.getElementById('performVerifyBtn')?.addEventListener('click', () => {
            this.performVerification();
        });

        shadow.querySelectorAll('.btn-copy[data-id]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const domainId = (btn as HTMLElement).getAttribute('data-id');
                const btnId = (btn as HTMLElement).id;
                if (domainId && btnId) {
                    this.copyToClipboard(domainId, btnId);
                }
            });
        });

        shadow.getElementById('copyTxtHost')?.addEventListener('click', () => {
            if (!this.verifyingDomain?.host) return;
            const parts = this.verifyingDomain.host.split('.');
            const sub = parts.length > 2 ? parts.slice(0, -2).join('.') : '@';
            const txtHost = sub === '@' ? '_crate-verify' : `_crate-verify.${sub}`;
            this.copyToClipboard(txtHost, 'copyTxtHost');
        });

        shadow.getElementById('copyTxtValue')?.addEventListener('click', () => {
            this.copyToClipboard(this.verifyingDomain?.verification_code || '', 'copyTxtValue');
        });

        shadow.getElementById('verificationGatewayTarget')?.addEventListener('change', (e: any) => {
            this._selectedVerificationGatewayId = e.target.value;
            
            const selectedGateway = this._selectedVerificationGatewayId === 'shared' ? null : this.gateways.find(g => g.id === this._selectedVerificationGatewayId);
            const isCname = this._selectedVerificationGatewayId === 'shared' || (selectedGateway && !selectedGateway.address?.match(/^[0-9.]+$/));
            const recordType = isCname ? 'CNAME' : 'A';
            const targetValue = this._selectedVerificationGatewayId === 'shared' ? 'us-east-1.gateway.crate.cc' : (selectedGateway?.address || '[Pending Node IP Assignment]');

            shadow.querySelectorAll('.record-type-label').forEach((el, index) => {
                if (index === 0) el.textContent = `${recordType} Host / Name`;
                if (index === 1) el.textContent = `${recordType} Value / Target`;
            });
            const targetValueEl = shadow.getElementById('dns-target-value');
            if (targetValueEl) targetValueEl.textContent = targetValue;
        });

        shadow.getElementById('copyCnameHost')?.addEventListener('click', () => {
            if (!this.verifyingDomain?.host) return;
            const parts = this.verifyingDomain.host.split('.');
            const cnameHost = parts.length > 2 ? parts.slice(0, -2).join('.') : '@';
            this.copyToClipboard(cnameHost, 'copyCnameHost');
        });

        shadow.getElementById('copyCnameValue')?.addEventListener('click', () => {
            const selectedGateway = this._selectedVerificationGatewayId === 'shared' ? null : this.gateways.find(g => g.id === this._selectedVerificationGatewayId);
            const targetValue = this._selectedVerificationGatewayId === 'shared' ? 'us-east-1.gateway.crate.cc' : (selectedGateway?.address || '[Pending Node IP Assignment]');
            this.copyToClipboard(targetValue, 'copyCnameValue');
        });
    }
}

if (!customElements.get('gateway-domains-tab')) {
    customElements.define('gateway-domains-tab', GatewayDomainsTab);
}

